import { ChartColumnBig } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
    OrganizationModuleType,
    isOrganizationModuleEnabled,
    isOrganizationModuleMember,
} from 'src/domain/organization';
import { isOrganizationAdministrator } from 'src/domain/user';
import { isMonthValue } from 'src/utils/norwegianMonth';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { OrganizationAnalyticsDashboard } from '../Admin/Analytics';
import { ModulePageLayout } from '../components/ModulePageLayout';
import { ModuleUnavailableState } from '../components/ModuleUnavailableState';

export const AnalyticsIcon = () => (
    <ChartColumnBig size={20} strokeWidth={1.8} aria-hidden="true" />
);

/**
 * The organization's Analytics module. Administrators always have access and can follow links
 * to the underlying lists; members see the same figures without links.
 */
export const AnalyticsScreen = () => {
    const { t } = useTranslation();
    const user = useSignedInUser();
    const { search } = useLocation();
    const { organizationId, organization, isLoading } =
        useCurrentOrganization();
    const moduleName = t('modules.analytics.navLabel');

    if (isLoading || !organization) {
        return (
            <div className="grid justify-center py-10 text-woodyBrown">
                {t('common.loading')}
            </div>
        );
    }

    if (
        !isOrganizationModuleEnabled(
            organization,
            OrganizationModuleType.analytics,
        )
    ) {
        return (
            <ModuleUnavailableState
                icon={AnalyticsIcon}
                moduleName={moduleName}
                reason="notEnabled"
            />
        );
    }

    // Global administrators in support mode see what the organization's administrators see.
    const isAdministrator =
        isOrganizationAdministrator(user, organizationId) ||
        Boolean(user?.supportMode);
    if (
        !isAdministrator &&
        !isOrganizationModuleMember(
            organization,
            OrganizationModuleType.analytics,
            user?.id,
        )
    ) {
        return (
            <ModuleUnavailableState
                icon={AnalyticsIcon}
                moduleName={moduleName}
                reason="accessRequired"
            />
        );
    }

    const month = new URLSearchParams(search).get('month');

    return (
        <ModulePageLayout
            basePath="/app/analytics"
            canManageSettings={!user?.supportMode && isAdministrator}
        >
            <div className="h-full overflow-y-auto pb-8">
                <OrganizationAnalyticsDashboard
                    organization={organization}
                    canOpenLinks={isAdministrator}
                    initialMonth={
                        isMonthValue(month) ? month ?? undefined : undefined
                    }
                />
            </div>
        </ModulePageLayout>
    );
};
